import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@common/checkbox";
import { Button } from "@common/button";
import { DateTimeInput } from "@common/dateTimeInput";
import {
  useExportData,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import {
  ExportsDataTypes,
  ExportsFormats,
  GanttFilter,
  Id,
  Identifiable,
  Role,
  SecondaryCriteria,
} from "@planzone/dto";
import { ColumnSelector } from "./columnSelector";
import { DataTypeFilter } from "./dataTypeFilter";
import { Props } from "./types";
import styles from "./styles.scss";

const defaultElementTypes: GanttFilter["elementTypes"] = [
  "list",
  "task",
  "milestone",
  "event",
];

function hasRole(role: Identifiable<Role>, roles: Id[]) {
  return !!roles.find((id) => id === role.uuid || id === role.name);
}

function isReadable(
  roles: Identifiable<Role>[],
  criteria: Identifiable<SecondaryCriteria>,
) {
  return !!roles.find((role) => hasRole(role, criteria.accessRoles.read));
}

function DailyPlanningReport(props: Props) {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [columns, setColumns] = useState<string[]>([]);
  const [elementTypes, setElementTypes] =
    useState<GanttFilter["elementTypes"]>(defaultElementTypes);
  const { isRequestPending, exportData } = useExportData();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    props.planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    props.project.uuid,
    "projectElement",
  );
  const roles = useRoles({ project: props.project });
  const template = [...planzoneTemplate, ...projectTemplate].filter(
    (criteria) => isReadable(roles, criteria),
  );

  return (
    <div
      className={classNames(styles.dailyPlanningReport, {
        [styles.checked]: isChecked,
      })}
    >
      <div className={styles.row}>
        <div
          className={styles.checkbox}
          onClick={() => setIsChecked(!isChecked)}
        >
          <Checkbox isChecked={isChecked} />
        </div>
        <div className={styles.col}>
          <h2 className={styles.name}>
            {t("exportReportsPage.dailyPlanningReport.title")}
          </h2>
          <p className={styles.description}>
            {t("exportReportsPage.dailyPlanningReport.subTitle")}
          </p>
        </div>
        {isChecked && (
          <Button
            className={styles.button}
            type="submit"
            status={isRequestPending ? "pending" : "none"}
            isDisabled={!startDate || !endDate}
            press={() =>
              exportData({
                fileName: props.fileName,
                author: props.author,
                data: {
                  type: ExportsDataTypes.PROJECT_DAILY_PLANNING_REPORT,
                  data: {
                    planzone: props.planzone,
                    project: props.project,
                    startDate,
                    endDate,
                    columns,
                    filter: {
                      scope: "project",
                      projects: [props.project],
                      elementTypes,
                    },
                    sort: "ascendingRank",
                    template,
                    roles,
                  },
                },
                format: ExportsFormats.EXCEL,
              })
            }
          >
            {t("exportReportsPage.download")}
          </Button>
        )}
      </div>
      {isChecked && (
        <div className={styles.filters}>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.dailyPlanningReport.startDate")}
            </h3>
            <DateTimeInput
              dateTime={startDate}
              placeholder={t("exportReportsPage.dailyPlanningReport.startDate")}
              input={setStartDate}
            />
          </div>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.dailyPlanningReport.endDate")}
            </h3>
            <DateTimeInput
              dateTime={endDate}
              placeholder={t("exportReportsPage.dailyPlanningReport.endDate")}
              input={setEndDate}
            />
          </div>
          <div className={styles.group}>
            <h3 className={styles.label}>
              {t("exportReportsPage.dailyPlanningReport.elementTypes")}
            </h3>
            <DataTypeFilter dataTypes={elementTypes} select={setElementTypes} />
          </div>
        </div>
      )}
      {isChecked && (
        <div className={styles.columns}>
          <h3 className={styles.label}>
            {t("exportReportsPage.dailyPlanningReport.columns")}
          </h3>
          <ColumnSelector
            project={props.project}
            template={template}
            columns={columns}
            setColumns={setColumns}
          />
        </div>
      )}
    </div>
  );
}

export { DailyPlanningReport };
